<template>
  <div v-if="!currentOrganizationId">Loading...</div>
  <BaseLayout
    v-else
    :title="pageTitle"
    :breadcrumb="[
      { label: 'Users', to: getRouteWithQuery(ROUTE_NAME.USER_LIST) },
      { label: userName || 'User Details' },
    ]"
  >
    <div class="max-w-5xl mx-auto">
      <!-- Header Row with Back Button and Actions -->
      <div class="flex items-center justify-between mb-4">
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          text
          @click="navigateToUserList"
        />
        <ActionsMenu
          v-if="user"
          :menu-items="actionMenuItems"
          :dangerous-action="dangerousAction"
        />
      </div>

      <!-- Loading State -->
      <AppCard v-if="isLoading" class="flex items-center justify-center py-12">
        <i class="pi pi-spin pi-spinner text-4xl text-gray-400" />
      </AppCard>

      <!-- Error States -->
      <AppCard v-else-if="notFoundError" class="text-center py-12">
        <div class="text-gray-500">
          <i
            class="pi pi-exclamation-circle text-4xl mb-4 block text-red-500"
          />
          <h3 class="text-lg font-medium mb-2">User Not Found</h3>
          <p class="text-sm mb-4">
            The user you're looking for doesn't exist or has been removed.
          </p>
          <Button
            label="Back to Users"
            icon="pi pi-arrow-left"
            @click="navigateToUserList"
          />
        </div>
      </AppCard>

      <AppCard v-else-if="fetchError" class="text-center py-12">
        <div class="text-gray-500">
          <i
            class="pi pi-exclamation-triangle text-4xl mb-4 block text-orange-500"
          />
          <h3 class="text-lg font-medium mb-2">Error Loading User</h3>
          <p class="text-sm mb-4">
            There was a problem loading the user details. Please try again.
          </p>
          <Button label="Retry" icon="pi pi-refresh" @click="loadUser" />
        </div>
      </AppCard>

      <!-- User Detail Component -->
      <div v-else-if="user" class="space-y-4">
        <!-- Archived Banner -->
        <AppCard v-if="!user.is_active" class="p-4">
          <div class="flex items-center justify-between">
            <p class="text-red-500 font-medium">
              This user is currently archived.
            </p>
            <Button
              label="Restore"
              severity="info"
              :loading="isUnarchiving"
              @click="handleUnarchive"
            />
          </div>
        </AppCard>

        <!-- Shared Account Banner -->
        <AppCard v-if="isSharedAccount" class="p-4 border-l-4 border-blue-500">
          <div class="flex items-center justify-between">
            <div class="flex items-start gap-2">
              <i class="pi pi-users text-blue-600 mt-0.5" />
              <p class="text-sm text-gray-700">
                This player shares a login with
                <strong>{{ contactName }}</strong
                >. They can be separated into their own accounts.
              </p>
            </div>
            <Button
              label="Create Separate Player Login"
              severity="info"
              size="small"
              @click="showSplitAccountDialog = true"
            />
          </div>
        </AppCard>

        <!-- Provisioning Banner (staff only, MAI-5051) -->
        <AppCard
          v-if="showProvisioningBanner"
          class="p-4 border-l-4 border-orange-400"
          data-testid="provisioning-banner"
        >
          <div class="flex items-center justify-between gap-4">
            <div class="flex items-start gap-2">
              <i class="pi pi-exclamation-triangle text-orange-500 mt-0.5" />
              <p class="text-sm text-gray-700">
                <strong>Not fully provisioned:</strong>
                <span v-if="!sendbirdSynced"> missing Sendbird user.</span>
                <span v-if="payabliNeeded"> missing Payabli customer.</span>
              </p>
            </div>
            <div class="flex gap-2 shrink-0">
              <Button
                v-if="!sendbirdSynced"
                label="Create Sendbird User"
                size="small"
                severity="warn"
                :loading="isEnsuringSendbird"
                data-testid="ensure-sendbird-user"
                @click="handleEnsureSendbirdUser"
              />
              <Button
                v-if="payabliNeeded"
                label="Create Payabli Customer"
                size="small"
                severity="warn"
                :loading="isEnsuringPayabli"
                data-testid="ensure-payabli-customer"
                @click="handleEnsurePayabliCustomer"
              />
            </div>
          </div>
        </AppCard>

        <!-- User Details -->
        <AppCard class="p-6">
          <UserDetail
            :user="user"
            :show-actions="false"
            :shared-profile-count="sharedProfiles.length"
            @edit="handleEdit"
            @set-password="handleSetPassword"
            @archive="handleArchive"
            @user-invited="handleUserInvited"
            @view-shared-profiles="showSharedProfilesDrawer = true"
            @reset-sms-consent="handleResetSmsConsent"
          />
        </AppCard>

        <!-- User Tabs -->
        <AppCard v-if="hasVisibleTabs">
          <Tabs v-model:value="activeTab" class="users-refresh-tabs">
            <!-- Tab bar row: pill tabs left, contextual action right (design) -->
            <div class="flex items-center justify-between gap-4">
              <TabList>
                <Tab
                  v-if="showRelationshipsTab"
                  :value="tabValues.relationships"
                  class="font-bold text-[var(--p-surface-700)]"
                  data-testid="relationships-tab"
                >
                  Relationships{{
                    relationshipsCount ? ` (${relationshipsCount})` : ""
                  }}
                </Tab>
                <Tab
                  v-if="showPlayerInfoTab"
                  :value="tabValues.info"
                  class="font-bold text-[var(--p-surface-700)]"
                  data-testid="info-tab"
                >
                  Info
                </Tab>
                <Tab
                  v-if="showWorkoutTab"
                  :value="tabValues.workout"
                  class="font-bold text-[var(--p-surface-700)]"
                >
                  Workout
                </Tab>
                <Tab
                  v-if="showBenefitsTab"
                  :value="tabValues.benefits"
                  class="font-bold text-[var(--p-surface-700)]"
                >
                  Benefits
                </Tab>
                <Tab
                  v-if="showPermissionsTab"
                  :value="tabValues.permissions"
                  class="font-bold text-[var(--p-surface-700)]"
                  data-testid="permissions-tab"
                >
                  Permissions
                </Tab>
              </TabList>
              <Button
                v-if="activeTab === tabValues.relationships"
                :label="addRelationshipLabel"
                icon="pi pi-plus"
                size="small"
                data-testid="add-relationship"
                @click="relationshipsTabRef?.handleAddClick($event)"
              />
            </div>

            <TabPanels>
              <TabPanel
                v-if="showRelationshipsTab"
                :value="tabValues.relationships"
                lazy
              >
                <RelationshipsTab
                  ref="relationshipsTabRef"
                  :user="user"
                  :organization-id="currentOrganizationId || 0"
                  @refresh-user="loadUser"
                />
              </TabPanel>

              <TabPanel v-if="showPlayerInfoTab" :value="tabValues.info">
                <PlayerInfoTab
                  v-if="currentOrganizationId"
                  :profile-id="user.id"
                  :organization-id="currentOrganizationId"
                  :player-data-api="playerDataApi"
                  @saving="isPlayerInfoSaving = $event"
                  @player-data-changed="
                    hasPlayerData = Object.keys($event || {}).length > 0
                  "
                />
              </TabPanel>

              <TabPanel v-if="showWorkoutTab" :value="tabValues.workout" lazy>
                <WorkoutTab :user-id="user.id" />
              </TabPanel>

              <TabPanel v-if="showBenefitsTab" :value="tabValues.benefits" lazy>
                <UserBenefitsTab
                  :profile-id="user.id"
                  :user-roles="user.roles"
                />
              </TabPanel>

              <TabPanel
                v-if="showPermissionsTab"
                :value="tabValues.permissions"
                lazy
              >
                <PermissionsTab
                  :user="user"
                  :organization-id="currentOrganizationId || 0"
                  @refresh-user="loadUser"
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </AppCard>
      </div>
    </div>

    <!-- Set Password Dialog -->
    <SetPasswordDialog
      v-if="user && showPasswordDialog"
      v-model="showPasswordDialog"
      :user-id="userId"
      @password-set="handlePasswordSet"
    />

    <!-- Edit Profile Drawer -->
    <UserEditDrawer
      v-if="user && showEditDrawer"
      v-model="showEditDrawer"
      :profile="user"
      :org-id="currentOrganizationId"
      @profile-updated="handleProfileUpdated"
    />

    <!-- Profile Picture Dialog -->
    <ProfilePictureDialog
      v-if="user && showProfilePictureDialog"
      v-model:visible="showProfilePictureDialog"
      :user-id="userId"
      :user-name="userName"
      :organization-id="currentOrganizationId || 0"
      :current-picture="user.picture"
      @picture-updated="handlePictureUpdated"
    />

    <!-- Split Shared Account Dialog -->
    <SplitSharedAccountDialog
      v-if="user && showSplitAccountDialog"
      v-model="showSplitAccountDialog"
      :profile-id="userId"
      :profile-name="userName"
      :current-email="sharedEmail || ''"
      :contact-name="contactName"
      @account-split="loadUser"
    />

    <!-- Other profiles on this email -->
    <SharedEmailProfilesDrawer
      v-if="sharedEmail"
      v-model:visible="showSharedProfilesDrawer"
      :email="sharedEmail"
      :profiles="sharedProfiles"
      :is-loading="isLoadingSharedProfiles"
      @select="handleSharedProfileSelect"
    />
  </BaseLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useConfirm } from "primevue/useconfirm";
import Tabs from "primevue/tabs";
import TabList from "primevue/tablist";
import Tab from "primevue/tab";
import TabPanels from "primevue/tabpanels";
import TabPanel from "primevue/tabpanel";
import { useToast } from "primevue/usetoast";
import { type WaffleAPI } from "@/shared/composables/useWaffle";
import * as defaultWaffleApi from "@/api/admin/admin";
import BaseLayout from "@/shared/components/BaseLayout.vue";
import AppCard from "@/shared/components/AppCard.vue";
import Button from "primevue/button";
import ActionsMenu from "@/shared/components/ActionsMenu.vue";
import UserDetail from "../components/UserDetail.vue";
import RelationshipsTab from "../components/RelationshipsTab.vue";
import PlayerInfoTab, {
  type PlayerDataAPI,
} from "@/modules/profile/components/PlayerInfoTab.vue";
import WorkoutTab from "../components/WorkoutTab.vue";
import UserBenefitsTab from "../components/UserBenefitsTab.vue";
import PermissionsTab from "../components/PermissionsTab.vue";
import SetPasswordDialog from "../components/SetPasswordDialog.vue";
import UserEditDrawer from "../components/UserEditDrawer.vue";
import ProfilePictureDialog from "../components/ProfilePictureDialog.vue";
import SplitSharedAccountDialog from "../components/SplitSharedAccountDialog.vue";
import SharedEmailProfilesDrawer from "../components/SharedEmailProfilesDrawer.vue";
import { useUserDetail } from "../composables/useUserDetail";
import { resetSmsConsent } from "../api";
import { useSharedEmailProfiles } from "../composables/useSharedEmailProfiles";
import { useArchiveUser } from "../composables/useArchiveUser";
import { useUnarchiveUser } from "../composables/useUnarchiveUser";
import { useBulkUserInvite } from "../composables/useBulkUserInvite";
import { useRouterNavigation } from "@/composables/useRouterNavigation";
import { useWaffle } from "@/shared/composables/useWaffle";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import { ROUTE_NAME } from "@/shared/constants/routes";
import { useAssociatedContactDetail } from "@/modules/payments/composables/useAssociatedContactDetail";
import * as defaultPlayerDataApi from "@/api/admin/admin";
import { usePlayerData } from "@/modules/profile/composables/usePlayerData";
import { downloadBlob, sanitizeFileName } from "@/shared/utils/helpers";
import { storeToRefs } from "pinia";
import { useAuthStore } from "@/modules/auth/stores/auth";
import { ensurePayabliCustomer, ensureSendbirdUser } from "../api";

const route = useRoute();
const confirm = useConfirm();
const { isTfaEmployee } = storeToRefs(useAuthStore());
const { navigateWithQuery, getRouteWithQuery } = useRouterNavigation();
const { currentOrganizationId } = useCurrentOrganization();

const props = withDefaults(
  defineProps<{
    waffleApi?: WaffleAPI;
    playerDataApi?: PlayerDataAPI;
  }>(),
  {
    waffleApi: () => defaultWaffleApi,
    playerDataApi: () => defaultPlayerDataApi,
  }
);

// Get user ID from route
const userId = computed(() => {
  const id = route.params.id;
  return typeof id === "string" ? parseInt(id, 10) : 0;
});

// Use composables
const { user, isLoading, fetchError, notFoundError, fetchUser } =
  useUserDetail();

const playerData = usePlayerData(props.playerDataApi);
const toast = useToast();
const waffle = useWaffle(props.waffleApi);
const { isArchiving, archiveUser } = useArchiveUser();
const { isUnarchiving, unarchiveUser } = useUnarchiveUser();
const { isInviting, inviteUsers } = useBulkUserInvite();
const { contactDetail, fetchContactDetail } = useAssociatedContactDetail();
const {
  profiles: sharedProfiles,
  isLoading: isLoadingSharedProfiles,
  fetchProfiles: fetchSharedProfiles,
} = useSharedEmailProfiles(currentOrganizationId);

// Dialog/Drawer state
const showPasswordDialog = ref(false);
const showEditDrawer = ref(false);
const showProfilePictureDialog = ref(false);
const showSplitAccountDialog = ref(false);
const showSharedProfilesDrawer = ref(false);
const isPlayerInfoSaving = ref(false);
const hasPlayerData = ref(false);
const isDownloadingCsv = ref(false);
const isEnsuringSendbird = ref(false);
const isEnsuringPayabli = ref(false);

// Computed properties
const userName = computed(() => user.value?.name || "");
const pageTitle = computed(() =>
  userName.value ? `User: ${userName.value}` : "User Details"
);

// Tab visibility logic
const isPlayer = computed(() => user.value?.roles?.includes("player"));
const isContact = computed(() => user.value?.roles?.includes("contact"));
const isCoach = computed(() => user.value?.is_coach || false);
const isSharedAccount = computed(
  () => isPlayer.value && user.value?.is_shared_account === true
);
const contactName = computed(
  () => contactDetail.value?.name || "their parent or guardian"
);
// The login this profile is reached by: their own email, or the guardian's when
// they have none of their own (managed athletes).
const sharedEmail = computed(
  () => user.value?.email || user.value?.shared_email || null
);

// Roleless users get the tab too (MAI-4998): adding a player is how they
// become a parent. Coach/admin-only users still don't.
const isRoleless = computed(
  () =>
    !isPlayer.value &&
    !isContact.value &&
    !isCoach.value &&
    !user.value?.is_admin
);
const showRelationshipsTab = computed(
  () => isPlayer.value || isContact.value || isRoleless.value
);
const showPlayerInfoTab = computed(
  () => isPlayer.value && currentOrganizationId.value
);
const showBenefitsTab = computed(() => true); // Show for all users (matching old form behavior)
const showWorkoutTab = computed(() => isPlayer.value || isCoach.value);
// "Staff" = anyone with coach or admin access (MAI-4955).
const showPermissionsTab = computed(
  () => isCoach.value || Boolean(user.value?.is_admin)
);

const relationshipsTabRef = ref<InstanceType<typeof RelationshipsTab> | null>(
  null
);

// Parent-only pages say "Add Player" (design); everyone else adds a
// relationship (dual-role users pick the direction from a menu).
const addRelationshipLabel = computed(() =>
  (isContact.value || isRoleless.value) && !isPlayer.value
    ? "Add Player"
    : "Add Relationship"
);

// Count for the tab label: the player's contacts plus (for contacts) the
// players they manage — both already on the profile payload.
const relationshipsCount = computed(() => {
  const contactsOfPlayer = isPlayer.value
    ? user.value?.associated_contacts_count || 0
    : 0;
  const playersOfContact = isContact.value
    ? user.value?.linked_player_profiles?.length || 0
    : 0;
  return contactsOfPlayer + playersOfContact;
});

const hasVisibleTabs = computed(
  () =>
    showRelationshipsTab.value ||
    showWorkoutTab.value ||
    showPlayerInfoTab.value ||
    showBenefitsTab.value
);

const tabValues = computed(() => {
  let index = 0;
  const values: Record<string, string> = {};

  if (showRelationshipsTab.value) {
    values.relationships = String(index++);
  }
  if (showPlayerInfoTab.value) {
    values.info = String(index++);
  }
  if (showWorkoutTab.value) {
    values.workout = String(index++);
  }
  if (showBenefitsTab.value) {
    values.benefits = String(index++);
  }
  if (showPermissionsTab.value) {
    values.permissions = String(index++);
  }

  return values;
});

// Active tab is controlled so links can deep-link to a specific tab via the
// `tab` query param (e.g. the booking drawer "Go to benefit" → Benefits tab).
const activeTab = ref("0");

watch(
  [() => route.query.tab, tabValues],
  ([requestedTab, values]) => {
    if (requestedTab === "benefits" && values?.benefits !== undefined) {
      activeTab.value = values.benefits;
    }
  },
  { immediate: true }
);

// Action menu items
const handleInviteToMobileApp = async () => {
  if (!user.value) return;
  const success = await inviteUsers([user.value]);
  if (success) {
    handleUserInvited();
  }
};

const downloadParticipantCSV = async () => {
  if (isDownloadingCsv.value) return;
  if (!user.value || !currentOrganizationId.value) return;

  isDownloadingCsv.value = true;
  try {
    await playerData.fetchCsv(currentOrganizationId.value, user.value.id);

    if (playerData.fetchCsvError.value) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail:
          playerData.fetchCsvError.value.message ||
          "Failed to download participant CSV",
        life: 10000,
      });
      return;
    }

    if (!playerData.csvDatum.value) {
      toast.add({
        severity: "info",
        summary: "Info",
        detail: "No CSV data available for participant",
        life: 3000,
      });
      return;
    }

    const sanitized = sanitizeFileName(user.value.name || "export");
    const filename = `participant-${sanitized}.csv`;
    downloadBlob(playerData.csvDatum.value, filename);
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "An unexpected error occurred while downloading the CSV",
      life: 10000,
    });
  } finally {
    isDownloadingCsv.value = false;
  }
};

// Managed athletes (MAI-4171) have no login (user is null), so
// login-only actions don't apply to them
const hasLogin = computed(() => !!user.value?.user);

const actionMenuItems = computed(() => [
  {
    label: "Edit",
    icon: "pi pi-pencil",
    command: () => handleEdit(),
  },
  {
    label: "Upload Profile Picture",
    icon: "pi pi-image",
    command: () => handleUploadPicture(),
  },
  ...(hasLogin.value
    ? [
        {
          label: "Set Password",
          icon: "pi pi-key",
          command: () => handleSetPassword(),
        },
        {
          label: "Invite to Mobile App",
          icon: "pi pi-send",
          command: handleInviteToMobileApp,
          disabled: isInviting.value,
        },
      ]
    : []),
  {
    label: "Download Participant CSV",
    icon: "pi pi-download",
    command: downloadParticipantCSV,
    dataTestId: "download-participant-csv",
    disabled:
      isPlayerInfoSaving.value ||
      !hasPlayerData.value ||
      isDownloadingCsv.value,
    disabledTooltip: !hasPlayerData.value ? "No player data available" : null,
    class: isDownloadingCsv.value ? "cursor-progress" : undefined,
  },
]);

// Staff-only provisioning repair (MAI-5051). Create-only: the buttons only
// appear when provisioning is missing, so an existing Sendbird user / Payabli
// customer can never be resynced from here.
// sendbird_user_created_at is hand-typed until the orval client regenerates.
const sendbirdSynced = computed(
  () =>
    !!(user.value as { sendbird_user_created_at?: string | null } | null)
      ?.sendbird_user_created_at
);
const payabliNeeded = computed(
  () =>
    hasLogin.value &&
    user.value?.organization?.payments_enabled === true &&
    !user.value?.billing_profile?.payabli_customer_id
);
const showProvisioningBanner = computed(
  () =>
    isTfaEmployee.value &&
    !!user.value?.is_active &&
    (!sendbirdSynced.value || payabliNeeded.value)
);

async function handleEnsureSendbirdUser() {
  isEnsuringSendbird.value = true;
  try {
    const result = await ensureSendbirdUser(userId.value);
    if ("error" in result) {
      toast.add({
        severity: "error",
        summary: "Sendbird",
        detail: result.error,
        life: 5000,
      });
      return;
    }
    toast.add({
      severity: "success",
      summary: "Sendbird",
      detail: result.already_existed
        ? "Sendbird user already exists — nothing to do."
        : "Sendbird user created.",
      life: 3000,
    });
    await loadUser();
  } finally {
    isEnsuringSendbird.value = false;
  }
}

async function handleEnsurePayabliCustomer() {
  isEnsuringPayabli.value = true;
  try {
    const result = await ensurePayabliCustomer(userId.value);
    if ("error" in result) {
      toast.add({
        severity: "error",
        summary: "Payabli",
        detail: result.error,
        life: 5000,
      });
      return;
    }
    toast.add({
      severity: "success",
      summary: "Payabli",
      detail: result.already_existed
        ? `Payabli customer already exists (#${result.payabli_customer_id}) — nothing to do.`
        : `Payabli customer provisioned (#${result.payabli_customer_id}).`,
      life: 3000,
    });
    await loadUser();
  } finally {
    isEnsuringPayabli.value = false;
  }
}

const dangerousAction = computed(() => ({
  label: "Archive",
  icon: "pi pi-trash",
  command: () => handleArchive(),
  dangerous: true as const,
}));

async function loadUser() {
  isPlayerInfoSaving.value = false;
  hasPlayerData.value = false;

  if (userId.value) {
    await fetchUser(userId.value);
    if (user?.value?.associated_contact_id) {
      await fetchContactDetail(user.value.associated_contact_id);
    }
    // Not gated on is_shared_account/shared_email: a parent's own profile has
    // neither set unless self-linked, and theirs is the page where "who else is
    // on this email" matters most.
    if (sharedEmail.value) {
      await fetchSharedProfiles(sharedEmail.value, userId.value);
    }
  }
}

onMounted(() => {
  loadUser();
  waffle.fetchStatus();
});

// Watch for route changes to refetch user
watch(
  () => route.params.id,
  (newId) => {
    if (newId) {
      loadUser();
    }
  }
);

// Action handlers
function handleEdit() {
  showEditDrawer.value = true;
}

function handleUploadPicture() {
  showProfilePictureDialog.value = true;
}

function handleSetPassword() {
  showPasswordDialog.value = true;
}

function handlePasswordSet() {
  // Optionally reload user data after password change
  console.log("Password set successfully for user:", userId.value);
}

function handleProfileUpdated() {
  // Reload user data after successful update
  loadUser();
}

function handlePictureUpdated() {
  // Reload user data to reflect updated profile picture
  loadUser();
}

function handleUserInvited() {
  // Reload user data to reflect updated mobile app status
  loadUser();
}

function handleArchive() {
  if (!user.value) return;

  confirm.require({
    message: `Are you sure you want to archive "${user.value.name}"?\n\nThis will prevent them from accessing the system and logging in. They will be deactivated immediately.\n\nThis does not cancel payment plans, subscriptions, or invoices — those must be canceled separately.`,
    header: "Archive User",
    icon: "pi pi-exclamation-triangle",
    rejectLabel: "Cancel",
    acceptLabel: "Archive User",
    rejectProps: {
      outlined: true,
    },
    acceptProps: {
      severity: "danger",
    },
    style: {
      maxWidth: "450px",
    },
    accept: async () => {
      const success = await archiveUser(
        userId.value,
        user.value?.organization?.id
      );
      if (success) {
        // Reload user data to show archived banner
        await loadUser();
      }
    },
  });
}

async function handleUnarchive() {
  if (!user.value) return;

  confirm.require({
    message: `They will regain access to the TFA mobile app.\n\nWould you like to restore them?`,
    header: "Restore User",
    icon: "pi pi-question-circle",
    rejectLabel: "Go back",
    acceptLabel: "Restore",
    rejectProps: {
      outlined: true,
    },
    acceptProps: {
      severity: "success",
    },
    style: {
      maxWidth: "450px",
    },
    accept: async () => {
      const success = await unarchiveUser(userId.value);
      if (success) {
        // Reload user data to reflect the updated status
        await loadUser();
      }
    },
  });
}

function navigateToUserList() {
  navigateWithQuery({
    name: ROUTE_NAME.USER_LIST,
  });
}

// The route-id watcher above reloads everything for the profile we land on.
function handleSharedProfileSelect(profileId: number) {
  showSharedProfilesDrawer.value = false;
  navigateWithQuery({
    name: ROUTE_NAME.USER_DETAIL,
    params: { id: String(profileId) },
  });
}

const smsConfirm = useConfirm();
const smsToast = useToast();
const handleResetSmsConsent = () =>
  smsConfirm.require({
    header: "Record new texting consent?",
    message:
      "Only do this with the person's written consent. TFA's record is cleared now; Twilio's STOP block stays until they text START to the number.",
    acceptLabel: "Record consent",
    rejectLabel: "Cancel",
    accept: async () => {
      if (!user.value) return;
      try {
        await resetSmsConsent(user.value.id);
        smsToast.add({
          severity: "success",
          summary: "Consent recorded",
          life: 3000,
        });
        await loadUser();
      } catch (error: any) {
        smsToast.add({
          severity: "error",
          summary: "Could not record consent",
          detail: error?.response?.data?.detail || error?.message,
          life: 6000,
        });
      }
    },
  });
</script>

<style scoped>
/* Pill-style tab bar per the Users Refresh design: gray track, white active
   pill, no underline ink-bar. Scoped to this page only. */
.users-refresh-tabs :deep(.p-tablist) {
  display: inline-flex;
  background: var(--p-surface-100);
  border-radius: 0.5rem;
  padding: 0.25rem;
}
.users-refresh-tabs :deep(.p-tablist-tab-list) {
  background: transparent;
  border: none;
  gap: 0.25rem;
}
.users-refresh-tabs :deep(.p-tab) {
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}
.users-refresh-tabs :deep(.p-tab-active) {
  background: var(--p-surface-0);
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.08);
}
.users-refresh-tabs :deep(.p-tablist-active-bar) {
  display: none;
}
</style>
